import {
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonCardTitle,
    IonCheckbox,
    IonCol,
    IonContent,
    IonIcon,
    IonModal,
    IonRow,
    IonTitle,
    IonToolbar,
} from "@ionic/react";
import React, { useEffect, useState } from "react";
import Spinner from "../Spinners/Spinner";
import { Barn, EventEntry, Horse, Membership, Person, S3Object } from "../../models";
import { Table } from "reactstrap";
import moment from "moment";
import ErrorAlert from "../Errors/ErrorAlert";
import { calculateHeightInHands } from "../../utilities/horse/Height";
import { deleteHorse, getHorsesByBarnId, updateHorse } from "../../utilities/horse/Horse";
import { getEventEntriesByHorseId, updateEventEntry } from "../../utilities/eventEntry/EventEntry";
import { getMembershipsByHorseId, updateMembership } from "../../utilities/membership/Membership";
import { getPersonByPersonId } from "../../utilities/person/Person";
import { formatDisplayNameForUser } from "../../utilities/person/PersonNameFormat";
import { checkmarkCircle, close, createOutline, removeCircleOutline, trashBinOutline } from "ionicons/icons";
import { UpdateEventEntryInput, UpdateHorseInput, UpdateMembershipInput } from "../../API";

interface _Props {
    barn?: (Barn | null | undefined)
}

interface FormattedHorse {
    id: string
    horse: Horse
    isSelected: boolean
    name: string
    nickname: string
    createdById: string
    createdByName: string
    ownerId: string
    ownerName: string
    contactId: string
    contact: string
    height: string
    hands?: string
    fraction?: string
    breed: string
    sex: string
    dateOfBirth: string
    color: string
    markings: string
    levelCompeted: string
    greenEligibility: string
    microchip: string
    passport: string
    cogginsNumber: string
    cogginsDate: string
    cogginsState: string
    cogginsKey: string
    cogginsS3Object?: S3Object
    entries: EventEntry[]
    memberships: Membership[]
}

const AdminMergeHorsesTable: React.FC<_Props> = ({barn}) => {    
    
    // Horse Details
    const [horses, setHorses] = useState<Horse[] | null | undefined>();
    const [formattedHorses, setFormattedHorses] = useState<FormattedHorse[] | null | undefined>();
    const [onlyShowSelectedHorses, setOnlyShowSelectedHorses] = useState(false);
    
    // Modals
    const [selectedFormattedHorse, setSelectedFormattedHorse] = useState<FormattedHorse | null | undefined>();
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Basic Details
    const [isLoadingHorses, setIsLoadingHorses] = useState(false);
    const [progressMessage, setProgressMessage] = useState("");
    const [horseError, setHorseError] = useState("");

    useEffect(() => {
        if (barn && !horses) getBarnHorses(barn);
    }, [barn]);

    const getBarnHorses = async (barn: Barn) => {
        setIsLoadingHorses(true);
        const queryResult = await getHorsesByBarnId(barn.id);
        if (queryResult.isSuccess) {
            const barnHorses: Horse[] = queryResult.result;
            setHorses(barnHorses);
            await formatBarnHorses(barnHorses);
        } else {
            setHorseError("Could not load the barn horses.");
        }
        setIsLoadingHorses(false);
    }
    
    const formatBarnHorses = async (barnHorses: Horse[]) => {
        let formattedHorses: FormattedHorse[] = [];
        if (barnHorses && barnHorses.length > 0) {
            for (let i = 0; i < barnHorses.length; i++) {
                const barnHorse = barnHorses[i];

                setProgressMessage(`Formatting horse #${i+1} out of ${barnHorses.length}.`);

                // Get the created by person associated with this horse
                let personName: string = "";
                if (barnHorse.personId) {
                    const personQuery = await getPersonByPersonId(barnHorse.personId);
                    if (personQuery.isSuccess) {
                        const person: Person = personQuery.result
                        personName = formatDisplayNameForUser(person);
                    }
                }

                const formattedHorse: FormattedHorse = {
                    id: barnHorse.id,
                    horse: barnHorse,
                    isSelected: false,
                    name: barnHorse.name,
                    nickname: barnHorse.nickname || "",
                    createdById: barnHorse.personId || "",
                    createdByName: personName || "",
                    ownerId: barnHorse.ownerId || "",
                    ownerName: barnHorse.owner?.name || barnHorse.ownerName || "",
                    contactId: barnHorse.contactId || "",
                    contact: barnHorse.contact ? (("Phone: " + (barnHorse.contact.home || barnHorse.contact.work || barnHorse.contact.cell)) + ("Email: " + (barnHorse.contact.workEmail || barnHorse.contact.personalEmail))) : "",
                    height: barnHorse.height ? (calculateHeightInHands(parseFloat(barnHorse.height)) + (barnHorse.fraction ? (" " + barnHorse.fraction) : "")) : "",
                    hands: barnHorse.height || undefined,
                    fraction: barnHorse.fraction || undefined,
                    breed: barnHorse.breed || "",
                    sex: barnHorse.sex || "",
                    dateOfBirth: barnHorse.dateOfBirth || "",
                    color: barnHorse.color || "",
                    markings: barnHorse.markings || "",
                    levelCompeted: barnHorse.highestLevelCompeted || "",
                    greenEligibility: barnHorse.greenEligibility || "",
                    microchip: barnHorse.microchipNumber || "",
                    passport: barnHorse.passportNumber || "",
                    cogginsNumber: barnHorse.cogginsNumber || "",
                    cogginsDate: barnHorse.cogginsDate || "",
                    cogginsState: barnHorse.cogginsState || "",
                    cogginsKey: barnHorse.coggins?.key || "",
                    cogginsS3Object: barnHorse.coggins || undefined,
                    entries: [],
                    memberships: []
                };
                formattedHorses.push(formattedHorse);
            }
        }
        setFormattedHorses(formattedHorses);
    }
    
    const handleHorseCheckBoxClick = (selectedFormattedHorse: FormattedHorse, isTurningOn: boolean, index: number) => {
        if (formattedHorses && formattedHorses.length > 0) {
            const updatedFormattedHorses: FormattedHorse[] = [
                ...formattedHorses.slice(0, index),
                {
                    ...selectedFormattedHorse,
                    isSelected: isTurningOn
                },
                ...formattedHorses.slice(index + 1),
            ];
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeSelectedHorsesClick = async () => {
        setIsLoadingHorses(true);
        setOnlyShowSelectedHorses(true);
        const filteredFormattedHorses: FormattedHorse[] = formattedHorses?.filter(h => h.isSelected) || [];

        let updatedFormattedHorses: FormattedHorse[] = [];
        for (let i = 0; i < filteredFormattedHorses.length; i++) {
            const horse = filteredFormattedHorses[i];

            setProgressMessage(`Getting entries and memberships for horse #${i+1} out of ${filteredFormattedHorses.length}.`);
            
            // Get the entries associated with this horse
            let entries: EventEntry[] = [];
            const entryQuery = await getEventEntriesByHorseId(horse.id);
            if (entryQuery.isSuccess) {
                entries = entryQuery.result;
            }

            // Get the entries associated with this horse
            let memberships: Membership[] = [];
            const membershipQuery = await getMembershipsByHorseId(horse.id);
            if (membershipQuery.isSuccess) {
                memberships = membershipQuery.result;
            }

            const updatedFormattedHorse: FormattedHorse = {
                ...horse,
                entries: entries,
                memberships: memberships
            };

            updatedFormattedHorses.push(updatedFormattedHorse);
        }

        setFormattedHorses(updatedFormattedHorses);
        setIsLoadingHorses(false);
    }

    const handleMergeHorseName = (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];
                const updated: FormattedHorse = {
                    ...fh,
                    name: selectedFormattedHorse.name
                };
                updatedFormattedHorses.push(updated);
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeHorseNickname = (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];
                const updated: FormattedHorse = {
                    ...fh,
                    nickname: selectedFormattedHorse.nickname
                };
                updatedFormattedHorses.push(updated);
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeHorseCreatedById = (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];
                const updated: FormattedHorse = {
                    ...fh,
                    createdById: selectedFormattedHorse.createdById,
                    createdByName: selectedFormattedHorse.createdByName,
                };
                updatedFormattedHorses.push(updated);
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeHorseOwnerId = (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];
                const updated: FormattedHorse = {
                    ...fh,
                    ownerId: selectedFormattedHorse.ownerId,
                    ownerName: selectedFormattedHorse.ownerName,
                };
                updatedFormattedHorses.push(updated);
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeHorseContactId = (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];
                const updated: FormattedHorse = {
                    ...fh,
                    contactId: selectedFormattedHorse.contactId,
                    contact: selectedFormattedHorse.contact,
                };
                updatedFormattedHorses.push(updated);
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeHorseHeight = (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];
                const updated: FormattedHorse = {
                    ...fh,
                    height: selectedFormattedHorse.height,
                    hands: selectedFormattedHorse.hands,
                    fraction: selectedFormattedHorse.fraction
                };
                updatedFormattedHorses.push(updated);
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeHorseBreed = (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];
                const updated: FormattedHorse = {
                    ...fh,
                    breed: selectedFormattedHorse.breed
                };
                updatedFormattedHorses.push(updated);
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeHorseSex = (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];
                const updated: FormattedHorse = {
                    ...fh,
                    sex: selectedFormattedHorse.sex
                };
                updatedFormattedHorses.push(updated);
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeHorseDateOfBirth = (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];
                const updated: FormattedHorse = {
                    ...fh,
                    dateOfBirth: selectedFormattedHorse.dateOfBirth
                };
                updatedFormattedHorses.push(updated);
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeHorseColor = (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];
                const updated: FormattedHorse = {
                    ...fh,
                    color: selectedFormattedHorse.color
                };
                updatedFormattedHorses.push(updated);
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeHorseMarkings = (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];
                const updated: FormattedHorse = {
                    ...fh,
                    markings: selectedFormattedHorse.markings
                };
                updatedFormattedHorses.push(updated);
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeHorseLevelCompeting = (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];
                const updated: FormattedHorse = {
                    ...fh,
                    levelCompeted: selectedFormattedHorse.levelCompeted
                };
                updatedFormattedHorses.push(updated);
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeHorseGreenEligibility = (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];
                const updated: FormattedHorse = {
                    ...fh,
                    greenEligibility: selectedFormattedHorse.greenEligibility
                };
                updatedFormattedHorses.push(updated);
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeHorseMicrochip = (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];
                const updated: FormattedHorse = {
                    ...fh,
                    microchip: selectedFormattedHorse.microchip
                };
                updatedFormattedHorses.push(updated);
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeHorsePassport = (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];
                const updated: FormattedHorse = {
                    ...fh,
                    passport: selectedFormattedHorse.passport
                };
                updatedFormattedHorses.push(updated);
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeHorseCogginsNumber = (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];
                const updated: FormattedHorse = {
                    ...fh,
                    cogginsNumber: selectedFormattedHorse.cogginsNumber
                };
                updatedFormattedHorses.push(updated);
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeHorseCogginsDate = (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];
                const updated: FormattedHorse = {
                    ...fh,
                    cogginsDate: selectedFormattedHorse.cogginsDate
                };
                updatedFormattedHorses.push(updated);
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeHorseCogginsState = (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];
                const updated: FormattedHorse = {
                    ...fh,
                    cogginsState: selectedFormattedHorse.cogginsState
                };
                updatedFormattedHorses.push(updated);
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeHorseCogginsKey = (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];
                const updated: FormattedHorse = {
                    ...fh,
                    cogginsKey: selectedFormattedHorse.cogginsKey,
                    cogginsS3Object: selectedFormattedHorse.cogginsS3Object
                };
                updatedFormattedHorses.push(updated);
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeHorseEntries = async (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];

                if (fh.id !== selectedFormattedHorse.id) {
                    // Update all entries with this horse
                    for (let j = 0; j < fh.entries.length; j++) {
                        const entry = fh.entries[j];
                        const updateEntryInput: UpdateEventEntryInput = {
                            id: entry.id,
                            horseId: selectedFormattedHorse.id,
                            horseName: selectedFormattedHorse.name
                        };
                        await updateEventEntry(updateEntryInput);
                    }

                    // Then update the formatted entry
                    const updated: FormattedHorse = {
                        ...fh,
                        entries: []
                    };
                    updatedFormattedHorses.push(updated);
                } else {
                    updatedFormattedHorses.push(fh);
                }
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleMergeHorseMemberships = async (selectedFormattedHorse: FormattedHorse) => {
        if (formattedHorses && formattedHorses.length > 0) {
            let updatedFormattedHorses: FormattedHorse[] = [];
            for (let i = 0; i < formattedHorses.length; i++) {
                const fh = formattedHorses[i];

                if (fh.id !== selectedFormattedHorse.id) {
                    // Update all memberships with this horse
                    for (let j = 0; j < fh.memberships.length; j++) {
                        const membership = fh.memberships[j];
                        const updateEntryInput: UpdateMembershipInput = {
                            id: membership.id,
                            horseId: selectedFormattedHorse.id
                        };
                        await updateMembership(updateEntryInput);
                    }

                    // Then update the formatted membership
                    const updated: FormattedHorse = {
                        ...fh,
                        memberships: []
                    };
                    updatedFormattedHorses.push(updated);
                } else {
                    updatedFormattedHorses.push(fh);
                }
            }
            setFormattedHorses(updatedFormattedHorses);
        }
    }

    const handleClickRemoveButton = (selected: FormattedHorse) => {
        setSelectedFormattedHorse(selected);
        setShowRemoveModal(true);
    }

    const handleClickDeleteButton = (selected: FormattedHorse) => {
        setSelectedFormattedHorse(selected);
        setShowDeleteModal(true);
    }

    const handleRemoveHorseFromBarn = async () => {
        setIsLoadingHorses(true);
        setShowRemoveModal(false);
        if (selectedFormattedHorse) {
            const updateInput: UpdateHorseInput = {
                id: selectedFormattedHorse.id,
                barnId: null,
                barnName: ""
            };
            await updateHorse(updateInput);

            const updatedFormattedHorses = formattedHorses?.filter(fh => fh.id !== selectedFormattedHorse.id);
            setFormattedHorses(updatedFormattedHorses);
        }
        setSelectedFormattedHorse(null);
        setIsLoadingHorses(false);
    }

    const handleDeleteHorse = async () => {
        setIsLoadingHorses(true);
        setShowDeleteModal(false);
        if (selectedFormattedHorse) {
            await deleteHorse({id: selectedFormattedHorse.id});

            const updatedFormattedHorses = formattedHorses?.filter(fh => fh.id !== selectedFormattedHorse.id);
            setFormattedHorses(updatedFormattedHorses);
        }
        setSelectedFormattedHorse(null);
        setIsLoadingHorses(false);
    }

    const handleFinishMergeClick = async () => {
        setIsLoadingHorses(true);

        // Update the currently selected formatted horses
        if (formattedHorses && formattedHorses.length > 0) {
            for (let index = 0; index < formattedHorses.length; index++) {
                const fh = formattedHorses[index];

                setProgressMessage(`Updating horse #${index+1} out of ${formattedHorses.length}.`);
                
                const updateHorseInput: UpdateHorseInput = {
                    id: fh.id,
                    name: fh.name || "",
                    nickname: fh.nickname || null,
                    personId: fh.createdById || null,
                    ownerId: fh.ownerId || null,
                    ownerName: fh.ownerName || "",
                    contactId: fh.contactId || null,
                    microchipNumber: fh.microchip || "",
                    passportNumber: fh.passport || "",
                    dateOfBirth: fh.dateOfBirth || null,
                    breed: fh.breed || "",
                    sex: fh.sex || "",
                    color: fh.color || "",
                    markings: fh.markings || "",
                    height: fh.hands || "",
                    fraction: fh.fraction || "",
                    cogginsNumber: fh.cogginsNumber || "",
                    cogginsDate: fh.cogginsDate || null,
                    cogginsState: fh.cogginsState || "",
                    coggins: fh.cogginsS3Object || null,
                    highestLevelCompeted: fh.levelCompeted || "",
                    greenEligibility: fh.greenEligibility || "",
                };
                await updateHorse(updateHorseInput);
            }
        }

        // Re-format the horses from the full list of barn horses
        if (horses) await formatBarnHorses(horses);

        // Re-set the screen
        setOnlyShowSelectedHorses(false);
        setIsLoadingHorses(false);
    }

    return (
        <>
            <IonRow className="ion-justify-content-center">
                <IonCol size="12">
                    <IonCard mode="md" className="ion-padding bg-white">
                        <IonCardTitle>Horses</IonCardTitle>
                        {isLoadingHorses ?
                            <IonRow className="ion-justify-content-center">
                                <IonCol size="12" className="ion-text-center">
                                    <Spinner />
                                    {progressMessage && <p>{progressMessage}</p>}
                                </IonCol>
                            </IonRow>
                            :
                            <>
                                <IonCardContent>
                                    {horseError && <ErrorAlert error={horseError} width="12" />}
                                    {(formattedHorses && formattedHorses.length > 0) ?
                                        <>
                                            <IonRow className="ion-justify-content-center">
                                                <IonCol className="ion-text-center">
                                                    {onlyShowSelectedHorses ?
                                                        <>
                                                            <p>Click on the values that you want to apply to the other horses. When you're finished, press the button below.</p>
                                                            <IonButton color="success" onClick={() => handleFinishMergeClick()}>Finish Merge</IonButton>
                                                        </>
                                                        :
                                                        <>
                                                            <p>Select any horses that you want to merge, then press the button below.</p>
                                                            <IonButton onClick={() => handleMergeSelectedHorsesClick()}>Merge Selected Horses</IonButton>
                                                        </>
                                                    }
                                                </IonCol>
                                            </IonRow>
                                            <Table striped responsive>
                                                <thead>
                                                    <tr>
                                                        {onlyShowSelectedHorses ? 
                                                            <>
                                                                <th>Delete</th>
                                                                <th>Remove</th>
                                                            </>
                                                            :
                                                            <th>Select</th>
                                                        }
                                                        <th>Id</th>
                                                        <th>Name {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                        <th>Nickname {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                        <th>Created By Id {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                        <th>Created By Name</th>
                                                        <th>Created On</th>
                                                        <th>Last Updated On</th>
                                                        <th>Owner Id {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                        <th>Owner Name</th>
                                                        <th>Contact Id {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                        <th>Contact</th>
                                                        <th>Height {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                        <th>Breed {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                        <th>Sex {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                        <th>DOB {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                        <th>Color {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                        <th>Markings {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                        <th>Level Competed {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                        <th>Green Elig. {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                        <th>Microchip {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                        <th>Passport {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                        <th>Coggins Number {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                        <th>Coggins Date {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                        <th>Coggins State {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                        <th>Coggins Key {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                        <th>Entries {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                        <th>Memberships {onlyShowSelectedHorses ? <IonIcon icon={createOutline} /> : ""}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {formattedHorses.map((formattedHorse, index) => (
                                                        <tr key={index}>
                                                            {onlyShowSelectedHorses ? 
                                                                <>
                                                                    <td>
                                                                        <p><IonButton color="danger" onClick={() => handleClickDeleteButton(formattedHorse)}><IonIcon icon={trashBinOutline}/></IonButton></p>
                                                                    </td>
                                                                    <td>
                                                                        <p><IonButton color="warning" onClick={() => handleClickRemoveButton(formattedHorse)}><IonIcon icon={removeCircleOutline}/></IonButton></p>
                                                                    </td>
                                                                </>
                                                                :
                                                                <td>
                                                                    <p><IonCheckbox onIonChange={e => handleHorseCheckBoxClick(formattedHorse, e.detail.checked, index)} /></p>
                                                                </td>
                                                            }
                                                            <td>
                                                                <p className="ion-text-wrap">{formattedHorse.id}</p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap" onClick={onlyShowSelectedHorses ? (() => handleMergeHorseName(formattedHorse)) : (() => {})}>
                                                                    {formattedHorse.name}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap" onClick={onlyShowSelectedHorses ? (() => handleMergeHorseNickname(formattedHorse)) : (() => {})}>
                                                                    {formattedHorse.nickname}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap" onClick={onlyShowSelectedHorses ? (() => handleMergeHorseCreatedById(formattedHorse)) : (() => {})}>
                                                                    {formattedHorse.createdById}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap">
                                                                    {formattedHorse.createdByName}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap">
                                                                    {formattedHorse.horse.createdOn ? moment(formattedHorse.horse.createdOn).format("MMM Do YYYY") : ""}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap">
                                                                    {formattedHorse.horse.updatedOn ? moment(formattedHorse.horse.updatedOn).format("MMM Do YYYY") : ""}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap" onClick={onlyShowSelectedHorses ? (() => handleMergeHorseOwnerId(formattedHorse)) : (() => {})}>
                                                                    {formattedHorse.ownerId}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap">
                                                                    {formattedHorse.ownerName}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap" onClick={onlyShowSelectedHorses ? (() => handleMergeHorseContactId(formattedHorse)) : (() => {})}>
                                                                    {formattedHorse.contactId}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap">
                                                                    {formattedHorse.contact}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap" onClick={onlyShowSelectedHorses ? (() => handleMergeHorseHeight(formattedHorse)) : (() => {})}>
                                                                    {formattedHorse.height}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap" onClick={onlyShowSelectedHorses ? (() => handleMergeHorseBreed(formattedHorse)) : (() => {})}>
                                                                    {formattedHorse.breed}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap" onClick={onlyShowSelectedHorses ? (() => handleMergeHorseSex(formattedHorse)) : (() => {})}>
                                                                    {formattedHorse.sex}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap" onClick={onlyShowSelectedHorses ? (() => handleMergeHorseDateOfBirth(formattedHorse)) : (() => {})}>
                                                                    {formattedHorse.dateOfBirth}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap" onClick={onlyShowSelectedHorses ? (() => handleMergeHorseColor(formattedHorse)) : (() => {})}>
                                                                    {formattedHorse.color}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap" onClick={onlyShowSelectedHorses ? (() => handleMergeHorseMarkings(formattedHorse)) : (() => {})}>
                                                                    {formattedHorse.markings}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap" onClick={onlyShowSelectedHorses ? (() => handleMergeHorseLevelCompeting(formattedHorse)) : (() => {})}>
                                                                    {formattedHorse.levelCompeted}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap" onClick={onlyShowSelectedHorses ? (() => handleMergeHorseGreenEligibility(formattedHorse)) : (() => {})}>
                                                                    {formattedHorse.greenEligibility}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap" onClick={onlyShowSelectedHorses ? (() => handleMergeHorseMicrochip(formattedHorse)) : (() => {})}>
                                                                    {formattedHorse.microchip}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap" onClick={onlyShowSelectedHorses ? (() => handleMergeHorsePassport(formattedHorse)) : (() => {})}>
                                                                    {formattedHorse.passport}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap" onClick={onlyShowSelectedHorses ? (() => handleMergeHorseCogginsNumber(formattedHorse)) : (() => {})}>
                                                                    {formattedHorse.cogginsNumber}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap" onClick={onlyShowSelectedHorses ? (() => handleMergeHorseCogginsDate(formattedHorse)) : (() => {})}>
                                                                    {formattedHorse.cogginsDate}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap" onClick={onlyShowSelectedHorses ? (() => handleMergeHorseCogginsState(formattedHorse)) : (() => {})}>
                                                                    {formattedHorse.cogginsState}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap" onClick={onlyShowSelectedHorses ? (() => handleMergeHorseCogginsKey(formattedHorse)) : (() => {})}>
                                                                    {formattedHorse.cogginsKey}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap">
                                                                    {formattedHorse.entries.length}
                                                                </p>
                                                                {onlyShowSelectedHorses && <IonButton color="success" onClick={() => handleMergeHorseEntries(formattedHorse)}><IonIcon icon={checkmarkCircle} /></IonButton>}
                                                            </td>
                                                            <td>
                                                                <p className="ion-text-wrap">
                                                                    {formattedHorse.memberships.length}
                                                                </p>
                                                                {onlyShowSelectedHorses && <IonButton color="success" onClick={() => handleMergeHorseMemberships(formattedHorse)}><IonIcon icon={checkmarkCircle} /></IonButton>}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </>
                                        :
                                        <p>No horses found.</p>
                                    }
                                </IonCardContent>
                            </>
                        }
                    </IonCard>
                </IonCol>
            </IonRow>

            {/* Remove Horse */}
            <IonModal backdropDismiss={false} isOpen={showRemoveModal}>
                <IonToolbar color="light">
                    <IonTitle className="ion-text-center">Remove Horse From Barn</IonTitle>
                    <IonButtons slot="end">
                        <IonButton
                            id="update-membership-modal-close-icon"
                            fill="clear"
                            onClick={() => setShowRemoveModal(false)}
                        >
                            <p
                                id="updateMembershipModalBtn"
                                className="font-weight-normal text-medium text-capitalize"
                            >
                                <IonIcon icon={close} />
                            </p>
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
                <IonContent className="ion-padding">
                    <IonRow className="ion-justify-content-center">
                        <IonCol className="ion-text-center">
                            <p>Are you sure you want to remove this horse from the barn?</p>
                        </IonCol>
                    </IonRow>
                    <IonRow>
                        <IonCol>
                            <p>Horse Id: {selectedFormattedHorse?.id || ""}</p>
                            <p>Horse Name: {selectedFormattedHorse?.name || ""}</p>
                            <p>Created By: {selectedFormattedHorse?.createdByName || ""}</p>
                        </IonCol>
                    </IonRow>
                    <IonRow className="ion-justify-content-center">
                        <IonCol className="ion-text-center">
                            <IonButton color="warning" onClick={() => handleRemoveHorseFromBarn()}>Remove</IonButton>
                        </IonCol>
                    </IonRow>
                </IonContent>
            </IonModal>

            {/* Delete Horse */}
            <IonModal backdropDismiss={false} isOpen={showDeleteModal}>
                <IonToolbar color="light">
                    <IonTitle className="ion-text-center">Delete Horse</IonTitle>
                    <IonButtons slot="end">
                        <IonButton
                            id="update-membership-modal-close-icon"
                            fill="clear"
                            onClick={() => setShowDeleteModal(false)}
                        >
                            <p
                                id="updateMembershipModalBtn"
                                className="font-weight-normal text-medium text-capitalize"
                            >
                                <IonIcon icon={close} />
                            </p>
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
                <IonContent className="ion-padding">
                    <IonRow className="ion-justify-content-center">
                        <IonCol className="ion-text-center">
                            <p>Are you sure you want to delete this horse from the database?</p>
                        </IonCol>
                    </IonRow>
                    <IonRow>
                        <IonCol>
                            <p>Horse Id: {selectedFormattedHorse?.id || ""}</p>
                            <p>Horse Name: {selectedFormattedHorse?.name || ""}</p>
                            <p>Created By: {selectedFormattedHorse?.createdByName || ""}</p>
                        </IonCol>
                    </IonRow>
                    <IonRow className="ion-justify-content-center">
                        <IonCol className="ion-text-center">
                            <IonButton color="danger" onClick={() => handleDeleteHorse()}>Delete</IonButton>
                        </IonCol>
                    </IonRow>
                </IonContent>
            </IonModal>
        </>
    );
};

export default AdminMergeHorsesTable;